import { useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";
import { pickAudioMime, AUDIO_MAX_BYTES, AUDIO_MAX_MS, fmtBytes } from "../lib/media";

const SILENCE_RMS = 0.015, SILENCE_MS = 2000, MAX_MS = AUDIO_MAX_MS;

export const Recorder = ({ onAudio, disabled, onPermissionDenied, compact = false }) => {
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const refs = useRef({});

  useEffect(() => {
    navigator.permissions?.query?.({ name: "microphone" }).then((p) => { if (p.state === "denied") onPermissionDenied?.(); }).catch(() => {});
  }, [onPermissionDenied]);

  const stop = () => {
    const r = refs.current;
    if (r.rec?.state === "recording") r.rec.stop();
    cancelAnimationFrame(r.raf); clearInterval(r.timer);
    r.stream?.getTracks().forEach((t) => t.stop()); r.ctx?.close();
    setRecording(false); setLevel(0);
  };

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 16000 } });
      const [mime, bps] = pickAudioMime();
      const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), audioBitsPerSecond: bps });
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: rec.mimeType || mime || "audio/webm" });
        console.info(`[audio] ${blob.type} ${fmtBytes(blob.size)} ${Math.round((Date.now() - startedAt) / 1000)}s @${bps}bps`);
        if (blob.size > AUDIO_MAX_BYTES) { setError(`Recording too large (${fmtBytes(blob.size)}) — keep notes under 90 s.`); return; }
        onAudio(blob);
      };
      const startedAt = Date.now();
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser(); analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      let lastVoice = Date.now();
      const tick = () => {
        analyser.getFloatTimeDomainData(buf);
        const rms = Math.sqrt(buf.reduce((s, v) => s + v * v, 0) / buf.length);
        setLevel(Math.min(1, rms * 8));
        if (rms > SILENCE_RMS) lastVoice = Date.now();
        if (Date.now() - lastVoice > SILENCE_MS && Date.now() - startedAt > 1500) return stop();
        if (Date.now() - startedAt > MAX_MS) return stop();
        refs.current.raf = requestAnimationFrame(tick);
      };
      refs.current = { rec, stream, ctx, timer: setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 500) };
      rec.start(250); setRecording(true); setElapsed(0); tick();
    } catch (e) {
      setError("Microphone unavailable — type it instead."); onPermissionDenied?.();
    }
  };

  if (compact) return (
    !recording
      ? <button type="button" data-testid="voice-search-button" className="icon-btn text-primary shrink-0" onClick={start} disabled={disabled} aria-label="Voice search"><Mic size={18} /></button>
      : <button type="button" data-testid="voice-search-stop" className="icon-btn recording-dot shrink-0" onClick={stop} aria-label="Stop"><Square size={14} fill="currentColor" /></button>
  );

  return (
    <div className="flex items-center gap-4" data-testid="recorder">
      {!recording ? (
        <button type="button" data-testid="record-start-button" className="btn-record" onClick={start} disabled={disabled}><Mic size={30} strokeWidth={2.5} /></button>
      ) : (
        <button type="button" data-testid="record-stop-button" className="btn-record recording" onClick={stop}><Square size={24} fill="currentColor" /></button>
      )}
      <div className="flex-1">
        {recording ? (
          <>
            <div className="level-meter" data-testid="level-meter"><div className="level-fill" style={{ transform: `scaleX(${level})` }} /></div>
            <p className="text-sm text-[#F6C945] font-semibold mt-1" data-testid="recording-indicator">● Recording {elapsed}s · stops after 2s silence</p>
          </>
        ) : <p className="text-sm text-muted">{error || "Tap, talk, we'll stop when you pause."}</p>}
      </div>
    </div>
  );
};
